import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { saveRconPresets, type RconPreset } from '../../api/rcon';
import { useSnackbar } from '../../context/SnackbarContext';

interface Props {
  open: boolean;
  gameId: string;
  initialPresets: RconPreset[];
  onClose: (saved: RconPreset[]) => void;
}

export function PresetManager({ open, gameId, initialPresets, onClose }: Props) {
  const [presets, setPresets] = useState<RconPreset[]>(initialPresets);
  const [newLabel, setNewLabel] = useState('');
  const [newCommand, setNewCommand] = useState('');
  const [saving, setSaving] = useState(false);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (open) setPresets(initialPresets);
  }, [open, initialPresets]);

  const handleAdd = () => {
    if (!newLabel.trim() || !newCommand.trim()) return;
    if (presets.length >= 20) return;
    setPresets((prev) => [...prev, { label: newLabel.trim(), command: newCommand.trim() }]);
    setNewLabel('');
    setNewCommand('');
  };

  const handleRemove = (idx: number) => {
    setPresets((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { presets: saved } = await saveRconPresets(gameId, presets);
      onClose(saved);
    } catch {
      showSnackbar('Failed to save presets', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(presets)} maxWidth="sm" fullWidth>
      <DialogTitle>Manage RCON Presets — {gameId}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {presets.length === 0 && (
            <Typography variant="body2" color="text.secondary">No presets yet.</Typography>
          )}
          {presets.map((p, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 500 }}>{p.label}</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1, color: 'text.secondary' }}>
                {p.command}
              </Typography>
              <IconButton size="small" onClick={() => handleRemove(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {presets.length < 20 && (
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
              <TextField
                size="small"
                label="Label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value.slice(0, 32))}
                sx={{ width: 130 }}
              />
              <TextField
                size="small"
                label="Command"
                value={newCommand}
                onChange={(e) => setNewCommand(e.target.value.slice(0, 256))}
                sx={{ flexGrow: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
              <IconButton
                size="small"
                onClick={handleAdd}
                disabled={!newLabel.trim() || !newCommand.trim()}
                color="primary"
                sx={{ mt: 0.5 }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(presets)}>Cancel</Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
